import { focusHaloFlush, panelMotion, press } from "@pax/ui";
import { max, min, sum } from "d3-array";
import { scaleLinear } from "d3-scale";
import { stack, stackOffsetDiverging } from "d3-shape";
import { type ReactNode, useId, useState } from "react";
import { formatCount } from "@/features/evals/components";
import { ChartAction } from "./ChartControls";
import { useDragZoom } from "./dragZoom";
import { FoldedBreakdown } from "./FoldedBreakdown";
import { highlights, useHighlight } from "./highlight";
import {
  type BucketStep,
  bucketInterval,
  type ChartBucket,
  type ChartSeries,
  chartHeight,
  chartMargin,
  chartWidth,
  EVERYTHING_ELSE,
  everythingElseShade,
  formatBucketStart,
  formatTimeTick,
  keepLargest,
  MOST_SERIES,
  shadeOf,
  svgId,
  timeScale,
} from "./shared";

/**
 * Keeps the series that move the most across the buckets, either way, in the order given, and adds
 * the rest together as `rest`, remembering which. A given series with the key of `rest` always joins
 * it. Of the rest, a series `pulled` names stands on its own just before `rest`, taken out of it.
 */
export function foldSmallestSeries(
  series: readonly ChartSeries[],
  buckets: readonly ChartBucket<number>[],
  rest: ChartSeries,
  pulled: (entry: ChartSeries) => boolean,
) {
  const named = series.flatMap((entry, index) => (entry.key === rest.key ? [] : [index]));
  const given = series.findIndex(entry => entry.key === rest.key);
  if (given < 0 && named.length <= MOST_SERIES) {
    return { series, buckets, folded: [], pulledCount: 0, restIndex: -1 };
  }
  const { kept, rest: past } = keepLargest(named, MOST_SERIES, index =>
    sum(buckets, bucket => Math.abs(bucket.values[index] ?? 0)),
  );
  const out = past.filter(index => pulled(series[index] as ChartSeries));
  const folded = [...past.filter(index => !out.includes(index)), ...(given < 0 ? [] : [given])];
  const shown = [...kept, ...out];
  return {
    series: [...shown.map(index => series[index] as ChartSeries), rest],
    buckets: buckets.map(bucket => ({
      start: bucket.start,
      values: [
        ...shown.map(index => bucket.values[index] ?? 0),
        sum(folded, index => bucket.values[index] ?? 0),
      ],
    })),
    folded,
    pulledCount: out.length,
    restIndex: shown.length,
  };
}

/** The share of the non-empty bars, by weight, that fit typical keeps whole; the rest is cut. */
const TYPICAL_SHARE = 0.9;

/**
 * The span the typical bars fill, capped at the reach most non-empty bars stay within, and whether
 * the cap cuts any bar; null with no bars to scale. Each bar counts once, or by its `weights`.
 */
export function typicalSpan(
  tops: readonly number[],
  bottoms: readonly number[],
  weights?: readonly number[],
) {
  const reaches = tops
    .map((top, index) => ({
      reach: Math.max(top, -(bottoms[index] ?? 0)),
      weight: weights?.[index] ?? 1,
    }))
    .filter(entry => entry.reach > 0 && entry.weight > 0)
    .sort((left, right) => left.reach - right.reach);
  const kept = sum(reaches, entry => entry.weight) * TYPICAL_SHARE;
  let counted = 0;
  let limit: number | undefined;
  for (const entry of reaches) {
    counted += entry.weight;
    limit = entry.reach;
    if (counted >= kept) break;
  }
  if (limit === undefined) return null;
  // A side with a bar cut off keeps room up to the cap, even when the typical bars sit on the other.
  return {
    cuts: reaches.some(entry => entry.reach > limit),
    top: tops.some(top => top > limit) ? limit : (max(tops) ?? 0),
    bottom: bottoms.some(bottom => -bottom > limit) ? -limit : (min(bottoms) ?? 0),
  };
}

/**
 * One bar per bucket, its series stacked first at the bottom. Values below zero stack downward from
 * the zero line. Hovering the folded rest, in a bar or the legend, lists what it holds. Hovering a
 * legend entry, or a tag in the same highlight scope, brings its series forward in every bar, and a
 * folded series hovered by its tag stands out of the rest meanwhile. With `onSelect`, clicking a bar
 * picks its bucket; with `onZoom`, dragging across picks a span.
 */
export function StackedBars({
  title,
  series: givenSeries,
  buckets: givenBuckets,
  step,
  summary,
  format,
  formatTick = formatCount,
  bucketSummary,
  rest = EVERYTHING_ELSE,
  onSelect,
  onZoom,
  fitted: givenFitted,
  onFittedChange,
  fitting = true,
}: {
  title: string;
  /** The first stacks at the bottom. Past the most a chart names, the ones that move least fold. */
  series: readonly ChartSeries[];
  /** The readout for a hovered bucket, before its time. Its total by default. */
  bucketSummary?: (bucket: number) => string;
  buckets: readonly ChartBucket<number>[];
  step: BucketStep;
  /** The readout while nothing is hovered. */
  summary: ReactNode;
  format: (value: number) => string;
  formatTick?: (value: number) => string;
  /** What the series past the most a chart names add up to, and its name. */
  rest?: ChartSeries;
  onSelect?: (bucket: number) => void;
  /** The span dragged across. */
  onZoom?: (from: number, to: number) => void;
  /** Whether "Fit typical" is on, for a caller that keeps it; the chart keeps it otherwise. */
  fitted?: boolean;
  onFittedChange?: (fitted: boolean) => void;
  /** Whether to offer "Fit typical", for bars whose spikes can dwarf the rest. */
  fitting?: boolean;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [foldedHovered, setFoldedHovered] = useState<"bucket" | "range" | null>(null);
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [ownFitted, setOwnFitted] = useState(false);
  const fitted = givenFitted ?? ownFitted;
  const setFitted = onFittedChange ?? setOwnFitted;
  const tagged = useHighlight().keys;
  const clipPrefix = svgId(useId());
  const dragZoom = useDragZoom(onZoom);
  const { series, buckets, folded, pulledCount, restIndex } = foldSmallestSeries(
    givenSeries,
    givenBuckets,
    rest,
    entry => tagged !== null && highlights(tagged, entry),
  );
  const highlightKeys = legendKey === null ? tagged : [legendKey];
  const forward = series.map(entry => highlightKeys !== null && highlights(highlightKeys, entry));
  const dimming = forward.some(Boolean);
  const starts = buckets.map(bucket => new Date(bucket.start));
  const first = starts[0];
  const last = starts.at(-1);
  if (!first || !last) return null;

  const interval = bucketInterval[step];
  const x = timeScale(step)
    .domain([first, interval.offset(last, 1)])
    .range([chartMargin.left, chartWidth - chartMargin.right]);
  const layers = stack<ChartBucket<number>, number>()
    .keys(series.map((_, index) => index))
    .value((bucket, index) => bucket.values[index] ?? 0)
    .offset(stackOffsetDiverging)(buckets);
  const tops = starts.map((_, index) => max(layers, layer => layer[index]?.[1]) ?? 0);
  const bottoms = starts.map((_, index) => min(layers, layer => layer[index]?.[0]) ?? 0);
  const typical = typicalSpan(tops, bottoms);
  const fit = fitted && typical?.cuts === true;
  const lowest = Math.min(0, (fit ? typical.bottom : min(bottoms)) ?? 0);
  const highest = Math.max(0, (fit ? typical.top : max(tops)) ?? 0);
  const y = scaleLinear()
    .domain([lowest, highest === lowest ? lowest + 1 : highest])
    .nice(4)
    .range([chartHeight - chartMargin.bottom, chartMargin.top])
    .clamp(true);
  // Only the top rounds out, so a sliver below zero doesn't add a whole empty tick under it.
  y.domain([lowest, y.domain()[1] ?? highest]);
  const band = (x(interval.offset(first, 1)) ?? 0) - (x(first) ?? 0);
  const gap = Math.min(4, band * 0.25);
  const barWidth = Math.max(band - gap, 0.5);
  const barLeft = (start: Date) => (x(start) ?? 0) + gap / 2;
  const totals = buckets.map(bucket => sum(bucket.values));
  const shownStart = hovered === null ? undefined : starts[hovered];
  const foldedIndex = restIndex;
  const pulledFrom = restIndex - pulledCount;
  const isPulled = (index: number) => index >= pulledFrom && index < restIndex;
  const shadeFill = (index: number) =>
    index === foldedIndex ? "" : isPulled(index) ? everythingElseShade.fill : shadeOf(index).fill;
  const pointer = onSelect ? "cursor-pointer" : "";

  const foldedItems = (value: (index: number) => number) =>
    folded
      .map(index => ({ index, value: value(index) }))
      .filter(item => item.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .map(item => ({
        key: givenSeries[item.index]?.key ?? String(item.index),
        label: givenSeries[item.index]?.label ?? "",
        value: format(item.value),
      }));
  const breakdown =
    foldedHovered === "range"
      ? {
          title: `${rest.label}, whole range`,
          items: foldedItems(index => sum(givenBuckets, bucket => bucket.values[index] ?? 0)),
        }
      : foldedHovered === "bucket" && hovered !== null && shownStart
        ? {
            title: `${rest.label}, ${formatBucketStart(shownStart, step)}`,
            items: foldedItems(index => givenBuckets[hovered]?.values[index] ?? 0),
          }
        : null;

  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm tabular-nums">
          {hovered !== null && shownStart
            ? `${bucketSummary ? bucketSummary(hovered) : format(totals[hovered] ?? 0)} · ${formatBucketStart(shownStart, step)}`
            : summary}
          {hovered !== null && onSelect && (
            <span className="text-muted-foreground"> · Click to see these changes</span>
          )}
        </p>
        {fitting && typical && (
          <ChartAction>
            <button
              type="button"
              aria-pressed={fit}
              title={
                fit ? "Scale to the tallest bar" : "Scale to the typical bars, cutting off the rest"
              }
              disabled={!typical.cuts}
              onClick={() => setFitted(!fit)}
              className={`shrink-0 disabled:pointer-events-none disabled:opacity-40 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap text-muted-foreground ring-1 ring-inset ring-foreground/15 hover:text-foreground ${panelMotion} ${press} ${focusHaloFlush}`}
            >
              {fit ? "Show all" : "Fit typical"}
            </button>
          </ChartAction>
        )}
      </div>
      {breakdown?.items.some(item => item.key !== rest.key) && <FoldedBreakdown {...breakdown} />}
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className={`mt-3 w-full overflow-visible ${onZoom ? "select-none" : ""}`}
        role="img"
        onMouseLeave={() => {
          setHovered(null);
          setFoldedHovered(null);
        }}
        {...dragZoom.svgProps(chartX => x.invert(chartX).valueOf())}
      >
        <title>{title}</title>
        <defs>
          <pattern
            id={`${clipPrefix}-hatch`}
            width={4}
            height={4}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={4}
              strokeWidth={1.5}
              className={everythingElseShade.hatch}
            />
          </pattern>
          {starts.map((start, index) => {
            const top = y(tops[index] ?? 0);
            const bottom = y(bottoms[index] ?? 0);
            return (
              <clipPath key={start.valueOf()} id={`${clipPrefix}-${index}`}>
                <rect
                  x={barLeft(start)}
                  y={top}
                  width={barWidth}
                  height={Math.max(bottom - top, 0)}
                  rx={Math.min(3, barWidth / 2)}
                />
              </clipPath>
            );
          })}
        </defs>
        {y.ticks(4).map(tick => (
          <g key={tick}>
            <line
              x1={chartMargin.left}
              x2={chartWidth - chartMargin.right}
              y1={y(tick)}
              y2={y(tick)}
              className="stroke-foreground/[0.07]"
            />
            <text
              x={chartMargin.left - 10}
              y={y(tick) + 4}
              fontSize={11}
              textAnchor="end"
              className="fill-muted-foreground tabular-nums"
            >
              {formatTick(tick)}
            </text>
          </g>
        ))}
        {x.ticks(6).map(tick => (
          <text
            key={tick.valueOf()}
            x={x(tick)}
            y={chartHeight - chartMargin.bottom + 18}
            fontSize={11}
            textAnchor="middle"
            className="fill-muted-foreground tabular-nums"
          >
            {formatTimeTick(tick, step)}
          </text>
        ))}
        {starts.map((start, index) => (
          // biome-ignore lint/a11y/noStaticElementInteractions: hovering changes the readout above, and clicking is a shortcut the history list also offers
          <rect
            key={start.valueOf()}
            x={x(start)}
            y={chartMargin.top}
            width={band}
            height={chartHeight - chartMargin.top - chartMargin.bottom}
            className={`fill-transparent ${pointer}`}
            onMouseEnter={() => {
              setHovered(index);
              setFoldedHovered(null);
            }}
            onClick={() => onSelect?.(index)}
          />
        ))}
        {starts.map((start, index) => (
          <g
            key={start.valueOf()}
            clipPath={`url(#${clipPrefix}-${index})`}
            className={`${panelMotion} ${hovered !== null && hovered !== index ? "opacity-50" : ""}`}
          >
            {layers.map((layer, seriesIndex) => {
              const [from, to] = layer[index] ?? [0, 0];
              const isFolded = seriesIndex === foldedIndex;
              return (
                // biome-ignore lint/a11y/noStaticElementInteractions: hovering changes the readout above, and clicking is a shortcut the history list also offers
                <rect
                  key={series[seriesIndex]?.key}
                  x={barLeft(start)}
                  y={y(Math.max(from, to))}
                  width={barWidth}
                  height={Math.abs(y(from) - y(to))}
                  className={`${panelMotion} ${pointer} ${shadeFill(seriesIndex)} ${dimming && !forward[seriesIndex] ? "opacity-25" : ""}`}
                  fill={isFolded ? `url(#${clipPrefix}-hatch)` : undefined}
                  onMouseEnter={() => {
                    setHovered(index);
                    setFoldedHovered(isFolded ? "bucket" : null);
                  }}
                  onClick={() => onSelect?.(index)}
                />
              );
            })}
          </g>
        ))}
        {fit &&
          starts.map((start, index) => {
            const above = (tops[index] ?? 0) > (y.domain()[1] ?? 0);
            const below = (bottoms[index] ?? 0) < (y.domain()[0] ?? 0);
            return (
              <g key={start.valueOf()} className="pointer-events-none">
                {above && (
                  <BreakMark left={barLeft(start)} width={barWidth} y={chartMargin.top + 6} />
                )}
                {below && (
                  <BreakMark
                    left={barLeft(start)}
                    width={barWidth}
                    y={chartHeight - chartMargin.bottom - 6}
                  />
                )}
              </g>
            );
          })}
        {dragZoom.band}
      </svg>
      {series.length > 1 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {series.map((entry, index) =>
            isPulled(index) ? null : (
              // biome-ignore lint/a11y/noStaticElementInteractions: hovering only brings the series forward and shows what the folded rest holds
              <span
                key={entry.key}
                className="flex items-center gap-1.5"
                onMouseEnter={() => {
                  setLegendKey(entry.key);
                  if (index === foldedIndex) setFoldedHovered("range");
                }}
                onMouseLeave={() => {
                  setLegendKey(null);
                  if (index === foldedIndex) setFoldedHovered(null);
                }}
              >
                <span
                  className={`size-2 rounded-[2px] ${index === foldedIndex ? everythingElseShade.swatch : shadeOf(index).swatch}`}
                />
                {entry.label}
                {hovered !== null && (
                  <span className="tabular-nums text-foreground">
                    {format(buckets[hovered]?.values[index] ?? 0)}
                  </span>
                )}
              </span>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/** Two slashes cut across a bar that runs past the axis, so it reads as cut off, not as that tall. */
export function BreakMark({ left, width, y }: { left: number; width: number; y: number }) {
  const slant = Math.min(3, width / 3);
  return (
    <>
      {[-2, 2].map(offset => (
        <line
          key={offset}
          x1={left - 1}
          x2={left + width + 1}
          y1={y + offset + slant}
          y2={y + offset - slant}
          strokeWidth={1.5}
          className="stroke-background"
        />
      ))}
    </>
  );
}
